"use client"

/**
 * A Google AdSense ad.
 *
 * Only response ads are supported.
 */

import { useEffect, useRef } from "react"

declare global {
  interface Window {
    adsbygoogle: unknown[]
  }
}

interface AdSlotProps {
  adSlot: string
  adFormat: "auto" | "fluid" | "rectangle" | "vertical" | "horizontal"
  style?: React.CSSProperties
  className?: string
}

export default function AdSenseAd({ adSlot, adFormat = "auto", style, className = "" }: AdSlotProps) {
  const adRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (adRef.current) {
      // Create fresh <ins> just in case
      // But that didn't help, switching between pages does not reload the ad.
      // We can repeat this by commenting out push, then trying to run
      // push from the browser console. This will not load ads, although it will
      // put in a div.

      // Clear the container
      adRef.current.innerHTML = ""

      // Create a completely new <ins> element
      const insElement = document.createElement("ins")
      insElement.className = `adsbygoogle ${className}`
      insElement.style.display = "block"

      // Apply custom styles
      if (style) {
        Object.assign(insElement.style, { ...style, overflow: "hidden" })
      }

      // Set AdSense attributes
      insElement.setAttribute("data-ad-client", "ca-pub-7591933378529904")
      insElement.setAttribute("data-ad-slot", adSlot)

      insElement.setAttribute("data-ad-format", adFormat)
      insElement.setAttribute("data-full-width-responsive", "true")

      // Add the fresh element to the container
      adRef.current.appendChild(insElement)

      // Inform AdSense we have a new ad if we have an offset.
      // If we don't, this is probably caused by us inserting an ad
      // for mobile on desktop, or vice versa, and then the ad isn't
      // visible. This is very tricky to solve especially for complex
      // layouts, so we simply remove those ads.
      if (insElement.offsetWidth) {
        try {
          ;(window.adsbygoogle = window.adsbygoogle || []).push({ google_ad_slot: adSlot })
        } catch (err) {
          console.debug("AdSense push error:", err)
        }
      } else {
        //console.debug("REMOVING AD WITH NO OFFSET", adSlot, adFormat)
        adRef.current.innerHTML = ""
      }
    }
  }, [adSlot, adFormat, style, className])

  return <div className="ad-slot" ref={adRef}></div>
}
